/**
 * Device information utilities
 * Parses user agent to extract platform, brand, and model information
 */

export interface DeviceInfo {
  agent: string;
  platform: string;
  platformDetailed: string; // Detailed platform with version and architecture
  brand: string;
  model: string;
  version?: string;
  architecture?: string;
  manufacturer?: string;
  userAgent: string;
  screenWidth: number;
  screenHeight: number;
}

/**
 * Parses the user agent string to extract device information
 * @param userAgent - The user agent string (defaults to navigator.userAgent)
 * @returns DeviceInfo object with platform, brand, model, and other info
 */
/**
 * Detects architecture from user agent
 */
const detectArchitecture = (ua: string): string => {
  // Check for ARM architecture
  if (/arm|aarch64/.test(ua)) {
    // Try to determine if 64-bit or 32-bit
    if (/arm64|aarch64/.test(ua)) {
      return '64 bit (ARM)';
    }
    return '32 bit (ARM)';
  }
  
  // Check for x86/x64 architecture
  if (/x64|x86_64|amd64|win64|wow64/.test(ua)) {
    return '64 bit (x64)';
  }
  
  if (/x86|win32|ia32/.test(ua)) {
    return '32 bit (x86)';
  }
  
  // Check for mobile architectures
  if (/mobile/.test(ua)) {
    // Most modern mobile devices are 64-bit
    if (/iphone|ipad/.test(ua)) {
      // iOS devices - check for newer devices (likely 64-bit)
      const osVersionMatch = ua.match(/iphone\s*os\s*(\d+)/);
      if (osVersionMatch) {
        const osVersion = parseInt(osVersionMatch[1], 10);
        // iOS 11+ devices are typically 64-bit
        return osVersion >= 11 ? '64 bit (ARM)' : '32 bit (ARM)';
      }
      return '64 bit (ARM)'; // Default for modern iOS
    }
    return '64 bit (ARM)'; // Default for modern Android
  }
  
  return 'Unknown';
};

/**
 * Formats detailed platform string
 */
const formatPlatformDetailed = (
  platform: string,
  version: string | undefined,
  architecture: string | undefined,
  manufacturer: string | undefined
): string => {
  const parts: string[] = [platform];
  
  if (version) {
    parts.push(version);
  }
  
  if (architecture && architecture !== 'Unknown') {
    parts.push(`(${architecture})`);
  }
  
  if (manufacturer) {
    parts.push(`by ${manufacturer}`);
  }
  
  return parts.join(' ');
};

export const parseDeviceInfo = (userAgent: string = navigator.userAgent): DeviceInfo => {
  const ua = userAgent.toLowerCase();
  
  let platform = 'Unknown';
  let brand = 'Unknown';
  let model = 'Unknown';
  let version: string | undefined;
  let architecture: string | undefined;
  let manufacturer: string | undefined;

  // Detect Platform
  if (/iphone|ipad|ipod/.test(ua)) {
    platform = 'iOS';
    brand = 'Apple';
    manufacturer = 'Apple Inc';
    
    // Extract iOS version
    const iosVersionMatch = ua.match(/iphone\s*os\s*(\d+)[_\.](\d+)(?:[_\.](\d+))?/);
    if (iosVersionMatch) {
      version = `${iosVersionMatch[1]}.${iosVersionMatch[2]}`;
      if (iosVersionMatch[3]) {
        version += `.${iosVersionMatch[3]}`;
      }
    }
    
    architecture = detectArchitecture(ua);
    
    // Extract iPhone model
    if (/iphone/.test(ua)) {
      // iPhone models
      if (/iphone\s*(\d+)/.test(ua)) {
        const modelMatch = ua.match(/iphone\s*(\d+)/);
        model = modelMatch ? `iPhone ${modelMatch[1]}` : 'iPhone';
      } else {
        model = 'iPhone';
      }
    } else if (/ipad/.test(ua)) {
      model = 'iPad';
    } else if (/ipod/.test(ua)) {
      model = 'iPod';
    }
  } else if (/android/.test(ua)) {
    platform = 'Android';
    
    // Extract Android version
    const androidVersionMatch = ua.match(/android\s+([\d.]+)/);
    if (androidVersionMatch) {
      version = androidVersionMatch[1];
    }
    
    architecture = detectArchitecture(ua);
    
    // Extract Android device info
    // Android user agents typically contain device model info
    const androidMatch = ua.match(/android\s+[\d.]+;\s*([^)]+)/);
    if (androidMatch && androidMatch[1]) {
      const deviceInfo = androidMatch[1].trim();
      
      // Try to extract brand and model
      // Common patterns: "SM-G973F" (Samsung), "Pixel 5" (Google), etc.
      const parts = deviceInfo.split(/\s+/);
      
      // Samsung devices often start with SM-
      if (/^sm-/.test(deviceInfo)) {
        brand = 'Samsung';
        manufacturer = 'Samsung Electronics';
        model = deviceInfo;
      }
      // Google Pixel devices
      else if (/pixel/.test(deviceInfo)) {
        brand = 'Google';
        manufacturer = 'Google Inc';
        model = deviceInfo.split(';')[0].trim();
      }
      // Xiaomi devices
      else if (/mi\s+\d+|redmi|poco/.test(deviceInfo)) {
        brand = 'Xiaomi';
        manufacturer = 'Xiaomi Inc';
        model = deviceInfo.split(';')[0].trim();
      }
      // OnePlus devices
      else if (/oneplus|one\s*plus/.test(deviceInfo)) {
        brand = 'OnePlus';
        manufacturer = 'OnePlus Technology';
        model = deviceInfo.split(';')[0].trim();
      }
      // Huawei devices
      else if (/huawei|honor/.test(deviceInfo)) {
        brand = 'Huawei';
        manufacturer = 'Huawei Technologies';
        model = deviceInfo.split(';')[0].trim();
      }
      // Generic Android - try to extract brand from first part
      else {
        const firstPart = parts[0];
        // If it looks like a model number, use it
        if (firstPart && firstPart.length > 0) {
          model = firstPart;
          // Try to infer brand from common patterns
          if (/^[A-Z]{2,3}-/.test(firstPart)) {
            brand = 'Samsung'; // Common Samsung pattern
            manufacturer = 'Samsung Electronics';
          } else {
            brand = 'Android Device';
            manufacturer = 'Google Inc'; // Default Android manufacturer
          }
        } else {
          model = deviceInfo;
          manufacturer = 'Google Inc';
        }
      }
    } else {
      brand = 'Android Device';
      manufacturer = 'Google Inc';
      model = 'Unknown Android';
    }
  } else if (/windows/.test(ua)) {
    platform = 'Windows';
    brand = 'Microsoft';
    manufacturer = 'Microsoft Corporation';
    
    architecture = detectArchitecture(ua);
    
    // Extract Windows version
    const winVersionMatch = ua.match(/windows\s*nt\s*(\d+)\.(\d+)/);
    if (winVersionMatch) {
      const major = parseInt(winVersionMatch[1], 10);
      const minor = parseInt(winVersionMatch[2], 10);
      
      if (major === 10) {
        // Try to extract build number for Windows 10/11
        const buildMatch = ua.match(/windows\s*nt\s*10\.0[;\s]+([^;)]+)/);
        if (buildMatch) {
          version = `10.0 (Build ${buildMatch[1].trim()})`;
        } else {
          version = '10.0';
        }
        model = 'Windows 10/11';
      } else if (major === 6) {
        if (minor === 3) {
          version = '6.3';
          model = 'Windows 8.1';
        } else if (minor === 2) {
          version = '6.2';
          model = 'Windows 8';
        } else if (minor === 1) {
          version = '6.1';
          model = 'Windows 7';
        } else {
          version = `${major}.${minor}`;
          model = `Windows NT ${major}.${minor}`;
        }
      } else {
        version = `${major}.${minor}`;
        model = `Windows NT ${major}.${minor}`;
      }
    } else {
      model = 'Windows';
    }
  } else if (/macintosh|mac\s*os/.test(ua)) {
    platform = 'macOS';
    brand = 'Apple';
    manufacturer = 'Apple Inc';
    
    architecture = detectArchitecture(ua);
    
    // Extract macOS version
    const macVersionMatch = ua.match(/mac\s*os\s*x\s*[_\s]*(\d+)[_\.](\d+)(?:[_\.](\d+))?/);
    if (macVersionMatch) {
      version = `${macVersionMatch[1]}.${macVersionMatch[2]}`;
      if (macVersionMatch[3]) {
        version += `.${macVersionMatch[3]}`;
      }
      model = `macOS ${version}`;
    } else {
      model = 'macOS';
    }
  } else if (/linux/.test(ua)) {
    platform = 'Linux';
    brand = 'Linux';
    manufacturer = 'Linux Foundation';
    
    architecture = detectArchitecture(ua);
    
    // Try to extract Linux distribution
    const distroMatch = ua.match(/(ubuntu|debian|fedora|centos|redhat|suse|arch|gentoo)/i);
    if (distroMatch) {
      model = distroMatch[1].charAt(0).toUpperCase() + distroMatch[1].slice(1);
    } else {
      model = 'Linux';
    }
  } else if (/chrome\s*os/.test(ua)) {
    platform = 'Chrome OS';
    brand = 'Google';
    manufacturer = 'Google Inc';
    
    architecture = detectArchitecture(ua);
    
    // Extract Chrome OS version
    const chromeOSVersionMatch = ua.match(/chrome\s*os\s*(\d+)[_\.](\d+)/);
    if (chromeOSVersionMatch) {
      version = `${chromeOSVersionMatch[1]}.${chromeOSVersionMatch[2]}`;
    }
    
    model = 'Chrome OS';
  }

  // Build detailed platform string
  const platformDetailed = formatPlatformDetailed(platform, version, architecture, manufacturer);

  const agent = userAgent.split(')')[0]?.trim().split('(')[1]?.trim() || 'Unknown';
  
  return {
    agent: agent,
    platform,
    platformDetailed,
    brand,
    model,
    version,
    architecture,
    manufacturer,
    userAgent: navigator.userAgent,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
  };
};

/**
 * Gets current device information
 * @returns DeviceInfo object for the current device
 */
export const getDeviceInfo = (): DeviceInfo => {
  return parseDeviceInfo();
};

/**
 * Gets a simplified device string for display
 * @param deviceInfo - Optional DeviceInfo object (defaults to current device)
 * @returns Formatted string like "iPhone 13 (Apple)" or "Galaxy S21 (Samsung)"
 */
export const getDeviceDisplayName = (deviceInfo?: DeviceInfo): string => {
  const info = deviceInfo || getDeviceInfo();
  return `${info.model} (${info.brand})`;
};

/**
 * Gets device info in the format used by the backend
 * @returns Object with userAgent, screenWidth, screenHeight, platform, platformDetailed, brand, model, version, architecture, manufacturer
 */
export const getDeviceInfoForBackend = () => {
  const info = getDeviceInfo();
  return {
    agent: info.agent,
    userAgent: info.userAgent,
    screenWidth: info.screenWidth,
    screenHeight: info.screenHeight,
    platform: info.platform,
    platformDetailed: info.platformDetailed,
    brand: info.brand,
    model: info.model,
    version: info.version,
    architecture: info.architecture,
    manufacturer: info.manufacturer,
  };
};

